import React, { useMemo } from "react";
import { OrgUnit } from "../../../../api/frameworksApi";
import { Dropdown } from "../../../components/Dropdown";

interface OrgCascadeFilterProps {
  parentOptions: OrgUnit[];
  orgId: string;
  divId: string;
  deptId: string;
  onOrgChange: (id: string) => void;
  onDivChange: (id: string) => void;
  onDeptChange: (id: string) => void;
  disabled?: boolean;
}

export const OrgCascadeFilter: React.FC<OrgCascadeFilterProps> = ({
  parentOptions,
  orgId,
  divId,
  deptId,
  onOrgChange,
  onDivChange,
  onDeptChange,
  disabled = false,
}) => {
  const orgOptions = useMemo(
    () => parentOptions.filter((o) => o.orgType === "ORGANIZATION"),
    [parentOptions]
  );

  const divOptions = useMemo(
    () =>
      orgId
        ? parentOptions.filter((o) => o.orgType === "DIVISION" && o.parentId === orgId)
        : [],
    [parentOptions, orgId]
  );

  const deptOptions = useMemo(
    () =>
      divId
        ? parentOptions.filter((o) => o.orgType === "DEPARTMENT" && o.parentId === divId)
        : [],
    [parentOptions, divId]
  );

  // An org is selected but it has no child divisions to filter by.
  const orgHasNoDivisions = Boolean(orgId) && divOptions.length === 0;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Organization filter — always visible */}
      <Dropdown
        variant="pill"
        className="min-w-[160px]"
        disabled={disabled}
        value={orgId}
        onChange={(v) => {
          onOrgChange(v);
          onDivChange("");
          onDeptChange("");
        }}
        options={[
          { value: "", label: "All Organizations" },
          ...orgOptions.map((o) => ({ value: o.id, label: o.name })),
        ]}
      />

      {/* Division filter — shown only when a specific org with divisions is selected.
          Hidden entirely while "All Organizations" is active. */}
      {orgId && divOptions.length > 0 && (
        <Dropdown
          variant="pill"
          className="min-w-[160px]"
          disabled={disabled}
          value={divId}
          onChange={(v) => {
            onDivChange(v);
            onDeptChange("");
          }}
          options={[
            { value: "", label: "All Divisions" },
            ...divOptions.map((o) => ({ value: o.id, label: o.name })),
          ]}
        />
      )}

      {/* No-divisions notice — replaces the Division filter when the selected
          organization has no divisions. */}
      {orgHasNoDivisions && (
        <span className="text-xs d whitespace-nowrap">
          No divisions are available for this organization.
        </span>
      )}

      {/* Department filter — shown only when a division with departments is selected. */}
      {divId && deptOptions.length > 0 && (
        <Dropdown
          variant="pill"
          className="min-w-[160px]"
          disabled={disabled}
          value={deptId}
          onChange={(v) => onDeptChange(v)}
          options={[
            { value: "", label: "All Departments" },
            ...deptOptions.map((o) => ({ value: o.id, label: o.name })),
          ]}
        />
      )}
    </div>
  );
};
